import type { FieldValue } from "../../types"

/** A leaf FieldValue has both a `value` and a trust `status`. */
function isFieldValue(node: unknown): node is FieldValue {
  return typeof node === "object" && node !== null && "status" in node && "value" in node
}

export type EditableCell = {
  /** Path from the section root, e.g. ["roles", "Buyer"] or ["items", "0", "value"]. */
  path: string[]
  label: string
  field: FieldValue
}

/**
 * Naive plural stripping turned "weaknesses" into "Weaknesse". Handle the -es
 * ending (sibilant stems) before the plain -s one.
 */
function singular(word: string): string {
  if (/(s|x|z|ch|sh)es$/i.test(word)) return word.slice(0, -2)
  return word.endsWith("s") ? word.slice(0, -1) : word
}

function humanise(key: string): string {
  const spaced = key.replace(/([a-z])([A-Z])/g, "$1 $2").replace(/[_-]+/g, " ")
  return spaced.charAt(0).toUpperCase() + spaced.slice(1)
}

/**
 * Every editable leaf in a composite section, depth-first.
 *
 * Walks generically rather than hardcoding each section, so buying, potential,
 * engage and strengthsWeaknesses all work from one implementation. Engage items
 * are `{label, value}` wrappers — their sibling `label` names the cell, which
 * reads far better than "Items 0".
 */
export function collectEditableCells(node: unknown, path: string[] = []): EditableCell[] {
  if (isFieldValue(node)) {
    const key = path[path.length - 1] ?? ""
    return [{ path, label: humanise(key), field: node }]
  }
  if (Array.isArray(node)) {
    return node.flatMap((item, index) => {
      const cells = collectEditableCells(item, [...path, String(index)])
      // `{label, value}` wrapper: name the cell after its own label.
      if (item && typeof item === "object" && "label" in item && "value" in item) {
        const label = String((item as { label?: unknown }).label ?? "").trim()
        if (label) return cells.map((cell) => ({ ...cell, label }))
      }
      // Bare list entries (strengths/weaknesses) read better as "Strength 1".
      return cells.map((cell) => ({
        ...cell,
        label: `${singular(humanise(path[path.length - 1] ?? "Item"))} ${index + 1}`,
      }))
    })
  }
  if (node && typeof node === "object") {
    return Object.entries(node).flatMap(([key, value]) =>
      collectEditableCells(value, [...path, key])
    )
  }
  return []
}

/** Immutably set a leaf FieldValue at `path` within a composite section. */
export function setCellValue(section: unknown, path: string[], next: FieldValue): unknown {
  if (path.length === 0) return next
  const [head, ...rest] = path
  if (Array.isArray(section)) {
    const index = Number(head)
    return section.map((item, i) => (i === index ? setCellValue(item, rest, next) : item))
  }
  if (section && typeof section === "object") {
    const source = section as Record<string, unknown>
    return { ...source, [head]: setCellValue(source[head], rest, next) }
  }
  return section
}

/** Display text for a cell, joining list values onto separate lines. */
export function cellText(field: FieldValue): string {
  return Array.isArray(field.value) ? field.value.filter(Boolean).join("\n") : field.value
}
